/**
 * EAS Dashboard Landing Page
 *
 * Overview of all EAS projects with quick navigation
 */

import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import toast from "react-hot-toast"

import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import { QuickStatsCard } from "./components/QuickStatsCard"
import { AddProjectModal } from "./components/AddProjectModal"
import { EditProjectModal } from "./components/EditProjectModal"

export function EASDashboardScreen() {
  const navigate = useNavigate()
  const [showAddProject, setShowAddProject] = useState(false)
  const [editingProject, setEditingProject] = useState<any>(null)

  // Fetch all projects
  const { data: projects, isLoading, refetch } = trpc.easUpdates.listProjects.useQuery()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground">Loading projects...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">EAS Update Dashboard</h1>
            <p className="text-muted-foreground mt-2">Centralized management for all your EAS projects</p>
          </div>
          <Button onClick={() => setShowAddProject(true)}>+ Add Project</Button>
        </div>

        {/* Quick Stats */}
        <QuickStatsCard projects={projects || []} />
      </div>

      {/* Projects Grid */}
      {projects && projects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-background border border-border rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer relative"
              onClick={() => navigate({ to: `/eas-dashboard/${project.slug}` })}
            >
              {/* Edit Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setEditingProject(project)
                }}
                className="absolute top-4 right-4 px-3 py-1 bg-secondary hover:bg-accent text-secondary-foreground text-xs rounded transition-colors"
              >
                Edit
              </button>

              {/* Project Header */}
              <div className="flex items-center justify-between mb-4 pr-16">
                <div className="flex items-center gap-3">
                  {project.icon && <span className="text-3xl">{project.icon}</span>}
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{project.name}</h3>
                    <p className="text-sm text-muted-foreground">@{project.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {project.isActive ? (
                    <span className="w-2 h-2 bg-green-500 rounded-full" title="Active" />
                  ) : (
                    <span className="w-2 h-2 bg-gray-400 rounded-full" title="Inactive" />
                  )}
                </div>
              </div>

              {/* Project Info */}
              {project.description && <p className="text-sm text-muted-foreground mb-4">{project.description}</p>}

              {/* Platforms & Channels */}
              <div className="flex flex-wrap gap-2 mb-4">
                {project.platforms.map((platform) => (
                  <span key={platform} className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded">
                    {platform === "ios" ? "📱 iOS" : "🤖 Android"}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {project.channels.slice(0, 3).map((channel) => (
                  <span key={channel} className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">
                    {channel}
                  </span>
                ))}
                {project.channels.length > 3 && (
                  <span className="px-2 py-1 bg-accent text-accent-foreground text-xs rounded">
                    +{project.channels.length - 3} more
                  </span>
                )}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  Created {new Date(project.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">No projects found</p>
          <Button onClick={() => setShowAddProject(true)}>+ Add Your First Project</Button>
        </div>
      )}

      {/* Add Project Modal */}
      {showAddProject && (
        <AddProjectModal
          onClose={() => setShowAddProject(false)}
          onSuccess={() => {
            setShowAddProject(false)
            void refetch()
            toast.success("Project added successfully!")
          }}
        />
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <EditProjectModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSuccess={() => {
            setEditingProject(null)
            void refetch()
          }}
        />
      )}
    </div>
  )
}
